import { visit } from 'unist-util-visit';
import type { Root } from 'mdast';
import type { Transformer } from 'unified';

export function remarkCustomAttributes(): Transformer<Root, Root> {
  return (tree) => {
    // Collect all dg directives with their full ancestor chain
    const directivesToProcess: Array<{ directive: any; ancestors: any[] }> = [];
    
    // Use a recursive function to track the full parent chain
    function walkTree(node: any, ancestors: any[] = []) {
      if (
        (node.type === 'containerDirective' ||
         node.type === 'leafDirective' ||
         node.type === 'textDirective') &&
        node.name === 'dg'
      ) {
        directivesToProcess.push({
          directive: node,
          ancestors: [...ancestors] // Copy the ancestors array
        });
      }
      
      if (node.children) {
        node.children.forEach((child: any) => {
          walkTree(child, [...ancestors, node]);
        });
      }
    }
    
    walkTree(tree);
    
    // Process each directive
    directivesToProcess.forEach(({ directive, ancestors }) => {
      const targetNode = findBestTargetNode(directive, ancestors);
      if (targetNode && directive.attributes) {
        Object.entries(directive.attributes).forEach(([key, value]) => {
          addDataAttribute(targetNode, key, value as string);
        });
      }
      
      // Remove directive from its immediate parent's children
      const immediateParent = ancestors[ancestors.length - 1];
      if (immediateParent?.children) {
        const index = immediateParent.children.indexOf(directive);
        if (index > -1) {
          // Check if previous sibling is a text node ending with space
          if (index > 0) {
            const prevSibling = immediateParent.children[index - 1];
            if (prevSibling && prevSibling.type === 'text' && prevSibling.value) {
              // Trim trailing space if the directive follows text
              prevSibling.value = prevSibling.value.replace(/ $/, '');
            }
          }
          immediateParent.children.splice(index, 1);
        }
      }
    });
    
    // Handle custom MDX components with custom attributes
    visit(tree, 'mdxJsxFlowElement', (node: any) => {
      if (node.attributes) {
        // Look for any custom attributes that should be transformed
        const customAttrs = node.attributes.filter((attr: any) => 
          attr.type === 'mdxJsxAttribute' && 
          isCustomAttribute(attr.name)
        );
        
        if (customAttrs.length > 0) {
          // Transform custom attributes to data-dg-* attributes
          customAttrs.forEach((attr: any) => {
            if (attr.value) {
              const value = typeof attr.value === 'string' ? attr.value : attr.value.value;
              addDataAttribute(node, attr.name, value);
            }
          });
          
          // Remove the original custom attributes
          node.attributes = node.attributes.filter((attr: any) => 
            !(attr.type === 'mdxJsxAttribute' && isCustomAttribute(attr.name))
          );
        }
      }
    });
    
    // Handle inline MDX components
    visit(tree, 'mdxJsxTextElement', (node: any) => {
      if (node.attributes) {
        const customAttrs = node.attributes.filter((attr: any) => 
          attr.type === 'mdxJsxAttribute' && 
          isCustomAttribute(attr.name)
        );
        
        if (customAttrs.length > 0) {
          customAttrs.forEach((attr: any) => {
            if (attr.value) {
              const value = typeof attr.value === 'string' ? attr.value : attr.value.value;
              addDataAttribute(node, attr.name, value);
            }
          });
          
          // Remove the original custom attributes
          node.attributes = node.attributes.filter((attr: any) => 
            !(attr.type === 'mdxJsxAttribute' && isCustomAttribute(attr.name))
          );
        }
      }
    });
  };
}

function addDataAttribute(node: any, key: string, value: string) {
  node.data = node.data || {};
  node.data.hProperties = node.data.hProperties || {};
  node.data.hProperties[`data-dg-${key}`] = value;
}

function findBestTargetNode(_directive: any, ancestors: any[]): any {
  // Find the best node to apply the directive attributes to
  // We work backwards through the ancestors to find the most appropriate target
  
  for (let i = ancestors.length - 1; i >= 0; i--) {
    const ancestor = ancestors[i];
    
    // If we find a list item, that's usually what we want for list-based directives
    if (ancestor.type === 'listItem') {
      return ancestor;
    }
    
    // For headings, paragraphs, blockquotes, these are good targets
    if (['heading', 'paragraph', 'blockquote'].includes(ancestor.type)) {
      return ancestor;
    }
  }
  
  // If we can't find a good ancestor, use the immediate parent
  return ancestors[ancestors.length - 1] || null;
}

function isCustomAttribute(name: string): boolean {
  // Define which attributes should be transformed to data-dg-*
  const customAttributes = [
    'id', 'color', 'priority', 'theme', 'style', 'category', 'type', 'level', 'status'
  ];
  return customAttributes.includes(name);
}